/**
 * How wide the floating recording bar may grow (#14933).
 *
 * The bar is `width: max-content` under ONE overall cap: the note detail
 * page's content column (`NOTE_CONTENT_MAX_WIDTH`), never wider than the pane
 * it centres in minus a gutter each side. Inside it every truncating text
 * part — the next meeting's title, the "Next: <title>" line, the Record
 * control's meeting name, the recording state's name slot — is a shrinkable
 * flex item whose only ceiling is derived from that cap. So a title shows in
 * full until the bar reaches its cap, and only then ellipsises.
 *
 * These replace the fixed per-part caps (a 200px name slot, a 160 / 96 / 64px
 * Record title) that cut ordinary meeting titles off at every width.
 */

import { NOTE_CONTENT_MAX_WIDTH } from '@/components/notes/note-content-width'

/**
 * The clear space kept between the bar and each side of its pane — the same
 * 1rem the bar's own popovers keep (`calc(100vw - 2rem)`).
 */
export const RECORDING_BAR_SIDE_GUTTER = '1rem'

/**
 * The bar's overall max width. `--app-sidecar-width` is the docked AI chat,
 * which `app-sidecar-clear-centered` already re-centres the bar beside; the
 * pane it centres in is that much narrower.
 */
export const RECORDING_BAR_MAX_WIDTH =
  `min(${NOTE_CONTENT_MAX_WIDTH}, calc(100vw - 2 * ` +
  `${RECORDING_BAR_SIDE_GUTTER} - var(--app-sidecar-width, 0px)))`

/**
 * The ceiling on any one text part: the bar's own cap. Flex shrinking is what
 * truncates a part before that, and only once the bar is at its cap.
 */
export const RECORDING_BAR_TEXT_MAX_WIDTH = RECORDING_BAR_MAX_WIDTH

/**
 * The recording state's name slot never narrows below this, so the "Open
 * note" button it swaps in on hover or focus fits under a short name without
 * the slot changing width.
 */
export const RECORDING_BAR_NAME_SLOT_MIN_WIDTH = '7rem'
